import { z } from 'zod';
import type { HostClassification } from '@playcastdotio/CoreTypes';
import { baseMessageSchema, createMessageId } from './_base';
import { clientTypeSchema } from './shared';

/**
 * `satisfies` rejects a value that is not a valid `HostClassification`. The
 * opposite direction is covered by the server, which passes a
 * `HostClassification` into the AUTH_SUCCESS generator: dropping a member here
 * would stop that call from compiling.
 */
const hostClassificationValues = ['internal', 'beta', 'wild', 'unknown'] as const satisfies readonly HostClassification[];

export enum AuthMessageType {
  AUTH_REQUEST = 'AUTH_REQUEST',
  AUTH_UPGRADE = 'AUTH_UPGRADE',
  AUTH_SUCCESS = 'AUTH_SUCCESS',
  AUTH_UPGRADE_SUCCESS = 'AUTH_UPGRADE_SUCCESS',
  AUTH_ERROR = 'AUTH_ERROR',
  AUTH_SIGNIN_TOKEN = 'AUTH_SIGNIN_TOKEN',
  AUTH_SIGNIN_TOKEN_REPLY = 'AUTH_SIGNIN_TOKEN_REPLY',
  AUTH_PROFILE_READY = 'AUTH_PROFILE_READY',
  AUTH_SESSION_TOKEN = 'AUTH_SESSION_TOKEN',
  AUTH_SESSION_TOKEN_SUCCESS = 'AUTH_SESSION_TOKEN_SUCCESS',
  /** Server → host: nonce for AIK connect proof (attestation SoT in realtime-api). */
  AUTH_CHALLENGE = 'AUTH_CHALLENGE',
}

/**
 * Canonical JSON object signed with the host AIK for connect / machine-owner proof.
 * Host app is a client only — ownership SoT is the attestation record in realtime-api.
 * Nonce SoT is the Redis challenge cache on realtime-api (one-time consume + TTL);
 * `issuedAt` is signed for audit only and must not be used for expiry (host clocks skew).
 * Auth error codes (enforced by realtime-api): NO_ATTESTATION, INVALID_ATTESTATION_PROOF, INVALID_MACHINE_ID.
 */
export const attestationConnectProofPayloadSchema = z.object({
  purpose: z.enum(['host_connect', 'machine_owner']),
  machineId: z.string().min(1),
  nonce: z.string().min(1),
  issuedAt: z.number(),
});

export const attestationConnectProofSchema = z.object({
  payload: attestationConnectProofPayloadSchema,
  signature: z.string().min(1),
});

export type AttestationConnectProofPayload = z.infer<typeof attestationConnectProofPayloadSchema>;
export type AttestationConnectProof = z.infer<typeof attestationConnectProofSchema>;

/**
 * Canonical JSON for AIK signing (sorted keys, no whitespace).
 * Shared by realtime-api verify, host-frontend sign, and node-service recover —
 * keep one implementation so Date/undefined/bigint edge cases cannot diverge.
 */
export const canonicalJson = (obj: unknown): string => {
  const sortKeys = (o: unknown): unknown => {
    if (Array.isArray(o)) {
      return o.map(sortKeys);
    }
    if (o !== null && typeof o === 'object') {
      return Object.keys(o as Record<string, unknown>)
        .sort()
        .reduce(
          (result, key) => {
            result[key] = sortKeys((o as Record<string, unknown>)[key]);
            return result;
          },
          {} as Record<string, unknown>,
        );
    }
    return o;
  };

  return JSON.stringify(sortKeys(obj));
};

// Payload schemas without the base message fields
const authRequestPayloadSchema = z.object({
  clientType: clientTypeSchema,
  tokenJwt: z.string().optional(),
  sessionToken: z.string().optional(),
  machineId: z.string().optional(),
  persistentId: z.string().optional(),
  anonymous: z.boolean().optional(), // For guest mode anonymous authentication
  cloudHostToken: z.string().optional(), // Shared secret for cloud host verification
  timezoneOffsetMinutes: z.number().optional(), // Client's Date.getTimezoneOffset() for local date computation
  /**
   * Present after AUTH_CHALLENGE (or pre-minted nonce).
   * Required when server attestationRequiredSinceInstalledRegistryVersion
   * applies to this host's installed registry version.
   */
  attestationProof: attestationConnectProofSchema.optional(),
});

const authChallengePayloadSchema = z.object({
  nonce: z.string().min(1),
  expiresAt: z.number(),
});

const authUpgradePayloadSchema = authRequestPayloadSchema;

const authSuccessPayloadSchema = z.object({
  userId: z.string(),
  isCloudHost: z.boolean().optional(), // Server-confirmed cloud host status
  // Server-resolved analytics segment, attached to host analytics events via the
  // identity store. Optional so older servers simply omit it.
  hostClassification: z.enum(hostClassificationValues).optional(),
});

const authUpgradeSuccessPayloadSchema = z.object({
  userId: z.string(),
});

const authErrorPayloadSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});

const authSigninTokenPayloadSchema = z.object({});

const authSigninTokenReplyPayloadSchema = z.object({
  token: z.string(),
  expiresIn: z.number(),
});

const authProfileReadyPayloadSchema = z.object({
  userId: z.string(),
  profileId: z.string(),
});

const authSessionTokenPayloadSchema = z.object({});

const authSessionTokenSuccessPayloadSchema = z.object({
  sessionToken: z.string(),
  expiresIn: z.number(),
});

// Full message schemas including base fields
const authRequestSchema = baseMessageSchema
  .extend({
    type: z.literal(AuthMessageType.AUTH_REQUEST),
  })
  .merge(authRequestPayloadSchema);

const authUpgradeSchema = baseMessageSchema
  .extend({
    type: z.literal(AuthMessageType.AUTH_UPGRADE),
  })
  .merge(authUpgradePayloadSchema);

const authSuccessSchema = baseMessageSchema
  .extend({
    type: z.literal(AuthMessageType.AUTH_SUCCESS),
  })
  .merge(authSuccessPayloadSchema);

const authUpgradeSuccessSchema = baseMessageSchema
  .extend({
    type: z.literal(AuthMessageType.AUTH_UPGRADE_SUCCESS),
  })
  .merge(authUpgradeSuccessPayloadSchema);

const authErrorSchema = baseMessageSchema
  .extend({
    type: z.literal(AuthMessageType.AUTH_ERROR),
  })
  .merge(authErrorPayloadSchema);

const authSigninTokenSchema = baseMessageSchema
  .extend({
    type: z.literal(AuthMessageType.AUTH_SIGNIN_TOKEN),
  })
  .merge(authSigninTokenPayloadSchema);

const authSigninTokenReplySchema = baseMessageSchema
  .extend({
    type: z.literal(AuthMessageType.AUTH_SIGNIN_TOKEN_REPLY),
  })
  .merge(authSigninTokenReplyPayloadSchema);

const authProfileReadySchema = baseMessageSchema
  .extend({
    type: z.literal(AuthMessageType.AUTH_PROFILE_READY),
  })
  .merge(authProfileReadyPayloadSchema);

const authSessionTokenSchema = baseMessageSchema
  .extend({
    type: z.literal(AuthMessageType.AUTH_SESSION_TOKEN),
  })
  .merge(authSessionTokenPayloadSchema);

const authSessionTokenSuccessSchema = baseMessageSchema
  .extend({
    type: z.literal(AuthMessageType.AUTH_SESSION_TOKEN_SUCCESS),
  })
  .merge(authSessionTokenSuccessPayloadSchema);

const authChallengeSchema = baseMessageSchema
  .extend({
    type: z.literal(AuthMessageType.AUTH_CHALLENGE),
  })
  .merge(authChallengePayloadSchema);

export const authMessageSchema = z.discriminatedUnion('type', [
  authRequestSchema,
  authUpgradeSchema,
  authSuccessSchema,
  authUpgradeSuccessSchema,
  authErrorSchema,
  authSigninTokenSchema,
  authSigninTokenReplySchema,
  authProfileReadySchema,
  authSessionTokenSchema,
  authSessionTokenSuccessSchema,
  authChallengeSchema,
]);

// Types for the payloads
export type AuthRequestPayload = z.infer<typeof authRequestPayloadSchema>;
export type AuthUpgradePayload = z.infer<typeof authUpgradePayloadSchema>;
export type AuthSuccessPayload = z.infer<typeof authSuccessPayloadSchema>;
export type AuthUpgradeSuccessPayload = z.infer<typeof authUpgradeSuccessPayloadSchema>;
export type AuthErrorPayload = z.infer<typeof authErrorPayloadSchema>;
export type AuthSigninTokenPayload = z.infer<typeof authSigninTokenPayloadSchema>;
export type AuthSigninTokenReplyPayload = z.infer<typeof authSigninTokenReplyPayloadSchema>;
export type AuthProfileReadyPayload = z.infer<typeof authProfileReadyPayloadSchema>;
export type AuthSessionTokenPayload = z.infer<typeof authSessionTokenPayloadSchema>;
export type AuthSessionTokenSuccessPayload = z.infer<typeof authSessionTokenSuccessPayloadSchema>;
export type AuthChallengePayload = z.infer<typeof authChallengePayloadSchema>;

// Type for the full messages
export type AuthMessage = z.infer<typeof authMessageSchema>;

type PayloadType<T extends AuthMessageType> = T extends AuthMessageType.AUTH_REQUEST
  ? AuthRequestPayload
  : T extends AuthMessageType.AUTH_UPGRADE
    ? AuthUpgradePayload
    : T extends AuthMessageType.AUTH_UPGRADE_SUCCESS
      ? AuthUpgradeSuccessPayload
      : T extends AuthMessageType.AUTH_SUCCESS
        ? AuthSuccessPayload
        : T extends AuthMessageType.AUTH_ERROR
          ? AuthErrorPayload
          : T extends AuthMessageType.AUTH_SIGNIN_TOKEN
            ? AuthSigninTokenPayload
            : T extends AuthMessageType.AUTH_SIGNIN_TOKEN_REPLY
              ? AuthSigninTokenReplyPayload
              : T extends AuthMessageType.AUTH_PROFILE_READY
                ? AuthProfileReadyPayload
                : T extends AuthMessageType.AUTH_SESSION_TOKEN
                  ? AuthSessionTokenPayload
                  : T extends AuthMessageType.AUTH_SESSION_TOKEN_SUCCESS
                    ? AuthSessionTokenSuccessPayload
                    : T extends AuthMessageType.AUTH_CHALLENGE
                      ? AuthChallengePayload
                      : never;

export function generateAuthMessage<T extends AuthMessageType>(type: T, payload: PayloadType<T>): AuthMessage {
  const base = {
    id: createMessageId(),
    timestamp: Date.now(),
    type,
  };

  switch (type) {
    case AuthMessageType.AUTH_REQUEST:
      return authRequestSchema.parse({
        ...base,
        ...payload,
      });
    case AuthMessageType.AUTH_UPGRADE:
      return authUpgradeSchema.parse({
        ...base,
        ...payload,
      });
    case AuthMessageType.AUTH_SUCCESS:
      return authSuccessSchema.parse({
        ...base,
        ...payload,
      });
    case AuthMessageType.AUTH_UPGRADE_SUCCESS:
      return authUpgradeSuccessSchema.parse({
        ...base,
        ...payload,
      });
    case AuthMessageType.AUTH_ERROR:
      return authErrorSchema.parse({
        ...base,
        ...payload,
      });
    case AuthMessageType.AUTH_SIGNIN_TOKEN:
      return authSigninTokenSchema.parse({
        ...base,
        ...payload,
      });
    case AuthMessageType.AUTH_SIGNIN_TOKEN_REPLY:
      return authSigninTokenReplySchema.parse({
        ...base,
        ...payload,
      });
    case AuthMessageType.AUTH_PROFILE_READY:
      return authProfileReadySchema.parse({
        ...base,
        ...payload,
      });
    case AuthMessageType.AUTH_SESSION_TOKEN:
      return authSessionTokenSchema.parse({
        ...base,
        ...payload,
      });
    case AuthMessageType.AUTH_SESSION_TOKEN_SUCCESS:
      return authSessionTokenSuccessSchema.parse({
        ...base,
        ...payload,
      });
    case AuthMessageType.AUTH_CHALLENGE:
      return authChallengeSchema.parse({
        ...base,
        ...payload,
      });
    default: {
      const exhaustiveCheck: never = type;
      throw new Error(`Unhandled message type: ${exhaustiveCheck}`);
    }
  }
}
